import React, { useState } from "react";
import DragEmailEditor from "./campaign/DragEmailEditor";
import HtmlEmailEditor from "./campaign/HtmlEmailEditor";

const API_URL = window.location.origin;
const MY_TPL_KEY = "sx_my_templates";

// Editor choices — same options as the campaign builder "Type" step.
const EDITORS = [
  {
    id: "drag",
    title: "Drag & drop editor",
    desc: "Effortlessly design beautiful emails with our simple drag-and-drop editor.",
    icon: "drag"
  },
  {
    id: "ai",
    title: "SendX AI Editor",
    desc: "A modern, block-based editor that makes writing and designing emails as seamless as typing a document.",
    icon: "ai",
    beta: true
  },
  {
    id: "html",
    title: "Plain Text / HTML editor",
    desc: "Write plain text or paste custom HTML for full control over your email design.",
    icon: "html"
  }
];

const LABELS = { drag: "Drag & Drop", ai: "SendX AI", html: "HTML / Code" };

function persistMyTemplate(tpl) {
  // Mirror the saved template into the campaign builder's "My Templates" store
  // so it also shows up when designing a campaign.
  try {
    const raw = localStorage.getItem(MY_TPL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(arr) ? arr : [];
    list.unshift(tpl);
    localStorage.setItem(MY_TPL_KEY, JSON.stringify(list));
  } catch (e) {
    /* ignore quota / parse errors */
  }
}

/*
 * Template creation flow used by the Settings > Templates tab.
 *  - kind="email"   -> first pick an editor (like campaign step 2 "Type"),
 *                      then open that editor. Saved to /api/templates AND to the
 *                      campaign builder's local template store.
 *  - kind="landing" -> opens the drag & drop editor directly.
 */
export default function TemplateBuilder({ kind, onClose, onSaved }) {
  const [editorType, setEditorType] = useState(kind === "landing" ? "drag" : "");
  const [saving, setSaving] = useState(false);

  async function handleSave(payload) {
    setSaving(true);
    const html = (payload && payload.html) || "";
    const title = (payload && payload.title) || "Untitled Template";
    try {
      await fetch(`${API_URL}/api/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: title,
          kind,
          type: kind === "landing" ? "Landing Page" : LABELS[editorType] || "Custom",
          html
        })
      });

      if (kind === "email") {
        persistMyTemplate({
          id: "tpl-" + Date.now(),
          title,
          category: (payload && payload.category) || "Custom",
          html,
          editorType,
          custom: true
        });
      }

      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      alert("Could not save template: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  // Step 1 (email only): choose the editor — mirrors the campaign "Type" step.
  if (kind === "email" && !editorType) {
    return (
      <div className="sx-modal-overlay" onMouseDown={onClose}>
        <div
          className="sx-type-modal"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="sx-type-modal-head">
            <h2>Select email editor</h2>
            <button type="button" className="sx-drawer-x" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="sx-type-card">
            {EDITORS.map((editor) => (
              <button
                type="button"
                key={editor.id}
                className="sx-editor-option"
                onClick={() => setEditorType(editor.id)}
              >
                <div className={`sx-editor-art ${editor.icon}`}>
                  {editor.icon === "drag" && (
                    <div className="sx-drag-icon">
                      <span></span>
                      <i></i>
                    </div>
                  )}
                  {editor.icon === "ai" && <div className="sx-ai-icon">✧</div>}
                  {editor.icon === "html" && <div className="sx-html-icon">I</div>}
                </div>
                <div className="sx-editor-info">
                  <h3>
                    {editor.title}
                    {editor.beta && <em>BETA</em>}
                  </h3>
                  <p>{editor.desc}</p>
                  <b>→</b>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Step 2: the chosen editor. HTML type uses the code editor, everything else
  // (drag / ai / landing pages) uses the drag & drop editor.
  if (editorType === "html") {
    return (
      <HtmlEmailEditor initial={{}} onSave={handleSave} onClose={onClose} />
    );
  }

  return (
    <DragEmailEditor initial={{}} onSave={handleSave} onClose={onClose} />
  );
}
